import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
  
import api from '../services/api';
// Create the context
const LeadReportContext = createContext();

export const useLeadReport = () => useContext(LeadReportContext);

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const retryApiCall = async (apiCall, retryCount = 0) => {
  try {
    return await apiCall();
  } catch (error) {
    const isCorsError = error.message?.includes('CORS') || 
                       error.message?.includes('Network Error') ||
                       error.message?.includes('Failed to fetch');

    if (isCorsError && retryCount < MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return retryApiCall(apiCall, retryCount + 1);
    }
    throw error;
  }
};

export const LeadReportProvider = ({ children }) => {
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchGeneralReport = useCallback(async (stats_type, startDate, endDate, teamFilter = []) => {
    try {
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      
      // Add team filter if provided
      if (teamFilter && teamFilter.length > 0) {
        requestData.team_filter = teamFilter;
      }
      
      const response = await retryApiCall(() => 
        api.post(`/report/lead/general-report/${stats_type}/`, requestData)
      );
      return response.data;
    } catch (error) {
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);


  const fetchStageFunnel = useCallback(async (startDate, endDate, teamFilter = []) => {
    try {
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      
      // Add team filter if provided
      if (teamFilter && teamFilter.length > 0) {
        requestData.team_filter = teamFilter;
      }
      
      const response = await retryApiCall(() => 
        api.post(`/report/lead/stage-funnel/`, requestData)
      );
      return response.data;
    } catch (error) {
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSourceFunnel = useCallback(async (startDate, endDate, teamFilter = []) => {
    try {
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      
      // Add team filter if provided
      if (teamFilter && teamFilter.length > 0) {
        requestData.team_filter = teamFilter;
      }
      
      const response = await retryApiCall(() => 
        api.post(`/report/lead/source-funnel/`, requestData)
      );
      return response.data;
    } catch (error) {
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);


  const fetchChannelPartnerAnalysis = useCallback(async (startDate, endDate) => {
    try {
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      const response = await retryApiCall(() => 
        api.post(`/report/lead/channel-partner-analysis/`, requestData)
      );
      return response.data;
    } catch (error) {
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);


  const fetchLeadSmartView = useCallback(async (startDate, endDate, page = 1, pageSize = 10, filters = {}) => {
    try {
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        },
        page: page,
        page_size: pageSize,
        ...filters
      };
      const response = await retryApiCall(() => 
        api.post(`/report/lead/smart-view/`, requestData)
      );
      return response.data;
    } catch (error) {
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSmartViewFilters = useCallback(async () => {
    const response = await retryApiCall(() => 
      api.get(`/report/lead/filters/`)
    );
    return response.data;
  }, [retryApiCall]);

  // Context value - memoized to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Core operations
    fetchGeneralReport,
    fetchStageFunnel, 
    fetchSourceFunnel,
    fetchChannelPartnerAnalysis,
    fetchLeadSmartView,
    fetchSmartViewFilters,
  }), [fetchGeneralReport, fetchStageFunnel, fetchSourceFunnel, fetchChannelPartnerAnalysis, fetchLeadSmartView, fetchSmartViewFilters]);

  return (
    <LeadReportContext.Provider value={value}>
      {children}
    </LeadReportContext.Provider>
  );
};

export default LeadReportProvider;
